import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { api, ApiError } from '../lib/api.js';

interface SetPasswordPageProps {
  onSuccess: () => void;
}

type TokenCheck = { status: 'checking' } | { status: 'invalid' } | { status: 'valid'; purpose: 'setup' | 'reset' };

const schema = z.object({ password: z.string().min(1), confirmPassword: z.string().min(1) });
type FormData = z.infer<typeof schema>;

// Mismo layout que LoginPage.tsx — no hay pattern de "auth screen" documentado
// en DESIGN_SYSTEM.md todavía, así que esta pantalla replica exactamente la
// estructura ya existente (card centrada, misma jerarquía tipográfica) en vez
// de inventar una nueva.
export function SetPasswordPage({ onSuccess }: SetPasswordPageProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [tokenCheck, setTokenCheck] = useState<TokenCheck>({ status: 'checking' });
  const [error, setError] = useState('');

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!token) {
      setTokenCheck({ status: 'invalid' });
      return;
    }
    api.get<{ valid: boolean; purpose: 'setup' | 'reset' | null }>(`/api/auth/set-password/status?token=${encodeURIComponent(token)}`)
      .then(res => setTokenCheck(res.valid && res.purpose ? { status: 'valid', purpose: res.purpose } : { status: 'invalid' }))
      .catch(() => setTokenCheck({ status: 'invalid' }));
  }, [token]);

  const onSubmit = async (data: FormData) => {
    setError('');
    if (data.password.length < 8) {
      setError(t('auth.passwordTooShort'));
      return;
    }
    if (data.password !== data.confirmPassword) {
      setError(t('auth.passwordsDontMatch'));
      return;
    }
    try {
      await api.post('/api/auth/set-password', { token, password: data.password });
      onSuccess();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'invalidOrExpiredToken';
      setError(t(`auth.${msg}`) !== `auth.${msg}` ? t(`auth.${msg}`) : t('auth.invalidOrExpiredTokenTitle'));
    }
  };

  if (tokenCheck.status === 'checking') {
    return (
      <div className="flex h-full items-center justify-center" style={{ background: 'var(--evo-bg)' }}>
        <LoadingState fullHeight label={t('auth.checkingToken')} />
      </div>
    );
  }

  if (tokenCheck.status === 'invalid') {
    return (
      <div className="flex h-full items-center justify-center" style={{ background: 'var(--evo-bg)' }}>
        <EmptyState
          title={t('auth.invalidOrExpiredTokenTitle')}
          description={t('auth.invalidOrExpiredTokenDescription')}
          action={<Button variant="solid" tone="cyan" onClick={() => { window.location.href = '/'; }}>{t('auth.backToLogin')}</Button>}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center" style={{ background: 'var(--evo-bg)' }}>
      <div className="card p-8 w-full max-w-sm">
        <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--evo-cyan)' }}>
          {t('auth.setPasswordTitle')}
        </h1>
        <p className="text-sm mb-6" style={{ color: 'var(--evo-fg-3)' }}>
          {tokenCheck.purpose === 'setup' ? t('auth.setPasswordSubtitleSetup') : t('auth.setPasswordSubtitleReset')}
        </p>

        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <Input
                type="password"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                autoComplete="new-password"
                label={t('auth.newPassword')}
                fullWidth
              />
            )}
          />

          <Controller
            name="confirmPassword"
            control={control}
            render={({ field }) => (
              <Input
                type="password"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                autoComplete="new-password"
                label={t('auth.confirmPassword')}
                fullWidth
              />
            )}
          />

          {error && (
            <p className="text-xs" style={{ color: 'var(--evo-danger)' }}>
              {error}
            </p>
          )}

          <Button type="submit" variant="solid" tone="cyan" fullWidth loading={isSubmitting}>
            {t('auth.setPasswordSubmit')}
          </Button>
        </form>
      </div>
    </div>
  );
}
